import * as React from 'react';
import {
  DataGridPremium,
  GridToolbar,
  GridRowsProp,
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
  GridColumnVisibilityModel,
  GridPaginationModel,
} from '@mui/x-data-grid-premium';
import { Link } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { UseQueryResult } from '@tanstack/react-query';

import { DateTime } from 'luxon';

import { QuestionMark } from '@mui/icons-material';
import { EventFormStatus, IApprovalListData } from '../core/api/clients';
import { Preference, usePreferences } from '../core/context/PreferencesContext';

function FlightOperationNotificationDataGrid({ data, reportType, readOnly, pageSize = 25 }: {
  data: UseQueryResult<IApprovalListData[], unknown>,
  reportType: string,
  readOnly?: boolean,
  pageSize?: number,
}) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Submitted': return 'warning';
      default: return undefined;
    }
  };

  const [hoverRow, setHoveredRow] = React.useState<GridValidRowModel | undefined>(undefined);
  const { dataGridDensity, updatePreference, flightOperationNotificationGridColumns } = usePreferences();
  const handlePopperClose = () => {
    setHoveredRow(undefined);
  };
  const displayChecked = (params: GridRenderCellParams<any, any, any>): string => {
    if (params.value === 'Yes' || params.value === true) {
      return 'YES';
    }

    if (params.value === undefined || params.value === 'NotApplicable' || params.value === 'Unknown') {
      return 'UNKNOWN';
    }

    return 'NO';
  };

  const columns: GridColDef[] = [
    { field: 'id' },
    {
      field: 'eventTitle',
      headerName: 'Event Title',
      renderCell: (params: GridRenderCellParams) => (
        <Link href={`form/${reportType}/${params.id}/view`} underline="hover">{params.value}</Link>
      ),
      flex: 1.5,
    },
    { field: 'eventType', headerName: 'Event Type', flex: 1 },
    { field: 'eventType2', headerName: 'Event Type 2', flex: 1 },
    { field: 'aircraftNumber', headerName: 'AC', flex: 0.5 },
    { field: 'flightNumber', headerName: 'Flight', flex: 0.5 },
    { field: 'departureAirport', headerName: 'DEP', flex: 0.5 },
    { field: 'scheduledArrivalAirport', headerName: 'ARR', flex: 0.5 },
    {
      field: 'eventDate',
      type: 'date',
      headerName: 'Event Date',
      valueGetter: (value) => DateTime.fromISO(value).toJSDate(),
      renderCell: (params: GridRenderCellParams) => (
        DateTime.fromJSDate(params.value).toLocaleString(DateTime.DATE_SHORT)
      ),
      flex: 0.75,
    },
    { field: 'dispatcher', headerName: 'Dispatcher', flex: 1 },
    { field: 'emergencyDeclared',
      headerName: 'Emergency',
      flex: 0.5,
      renderCell: (params) => (displayChecked(params)) },
    { field: 'derInitiated',
      headerName: 'DER',
      flex: 0.25,
      renderCell: (params) => (displayChecked(params)) },
    { field: 'serInitiated',
      headerName: 'SER',
      flex: 0.25,
      renderCell: (params) => (displayChecked(params)) },
  ];

  const [dataRows, setDataRows] = React.useState<GridRowsProp>([]);
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel | undefined>({
    pageSize,
    page: 0,
  });

  const updDataRows = (newData: IApprovalListData[]) => {
    setDataRows([...newData.sort((a, b) => {
      if (a.status !== b.status) {
        switch (a.status) {
          case EventFormStatus.Submitted:
            return -1;
          case EventFormStatus.Approved:
            return 1;
          case EventFormStatus.Final:
            return 2;
          case EventFormStatus.Deleted:
            return 3;
          default:
        }
      }

      return DateTime.fromISO(b.eventDate!).toMillis() - DateTime.fromISO(a.eventDate!).toMillis();
    })]);
  };
  React.useEffect(() => {
    const areObjectsEqual = JSON.stringify(flightOperationNotificationGridColumns) === JSON.stringify({});
    if (!flightOperationNotificationGridColumns || areObjectsEqual) {
      updatePreference(Preference.FlightOperationNotificationGridColumns, {
        id: false, derInitiated: false, serInitiated: false,
      });
    }
  }, []);
  React.useEffect(() => {
    if (data.status === 'success' && data.data !== undefined) {
      updDataRows(data.data);
    }
    return () => {
      setDataRows([]);
    };
  }, [data]);

  return (
    <DataGridPremium
      autoHeight
      density={dataGridDensity}
      onDensityChange={(density) => updatePreference(Preference.DataGridDensity, density)}
      pagination
      paginationModel={paginationModel}
      rows={dataRows}
      columns={columns}
      rowHeight={35}
      pageSizeOptions={[10, 25, 50]}
      columnVisibilityModel={flightOperationNotificationGridColumns}
      onPaginationModelChange={setPaginationModel}
      onColumnVisibilityModelChange={(model) => {
        updatePreference(Preference.FlightOperationNotificationGridColumns, model);
      }}
      slots={{
        toolbar: GridToolbar,
      }}
      slotProps={{
        row: {
          onMouseOver: (event: any): void => {
            const { id } = event.currentTarget.dataset;

            if (hoverRow === undefined || hoverRow.id.toString() !== id) {
              const row = dataRows.find((x) => x.id.toString() === id);
              setHoveredRow(row);
            }
          },
          onMouseLeave: handlePopperClose,
        },
      }}
    />
  );
}

export default FlightOperationNotificationDataGrid;
